import React, { useEffect, useState, useMemo, useRef } from "react";
import { useConsultantVisibleContext } from "../../../features/consultant/screenContextAdapter";
import { useSelector } from "react-redux";
import "./energy_matrix.css";

import { getEnergyMatrixSummary } from "../../../redux/apiCalls/analysisCall/energyMatrixCalls";
import { Toast } from "../../../helpers/alert";

import DragDropCompanies from "../../../components/Measurement/DragDropCompanies";
import FrequencyComponent from "../../../components/FilterFrequency/FilterFrequency";
import KpiCard from "./KpiCard";
import ElectricityInfo from "./ElectricityInfo";
import EnergyMixDonut from "../../../components/Charts/EnergyMixDonut";
import CostMixDonut from "../../../components/Charts/CostMixDonut";
import CarrierTable from "./CarrierTable";
import DriversWaterfall from "./DriversWaterfall";
import BranchBenchmarkTable from "./BranchBenchmarkTable";
import SunburstChart from "../../../components/Charts/SunburstChart";
import { Check } from "lucide-react";
import { useImageExport } from "../../../hooks/useImageExport";
import ChartControls from "../../../components/Measurement/ChartControls";
import DeviceGraph from "./DeviceGraph";

const formatDate = (d) => {
  if (!d) return "";
  return d.toLocaleDateString("es-AR");
};
const EnergyMatrix = () => {
  const userAccessToken = useSelector(
    (state) => state.user.currentUser?.accessToken,
  );

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [firstSelect, setFirstSelect] = useState([]);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [compareStartDate, setCompareStartDate] = useState(null);
  const [compareEndDate, setCompareEndDate] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  useConsultantVisibleContext({ firstSelect, startDate, endDate,
    active_tab: showComparison ? "Comparación" : "Matriz energética",
    comparison_start: compareStartDate?.toISOString(), comparison_end: compareEndDate?.toISOString() });

  const [showReportInput, setShowReportInput] = useState(false);
  const [buttonVisible, setButtonVisible] = useState(true);

  const graphRef = useRef(null);
  const exportNameRef = useRef(null);

  // INIT
  useEffect(() => {
    try {
      const stored = localStorage.getItem("measurement_firstSelect");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setFirstSelect(parsed);
        }
      }
    } catch (e) {
      console.error("Error loading branch selection", e);
    }

    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    setStartDate(firstDayOfMonth);
    setEndDate(today);
  }, []);

  // DERIVED STATE
  const branchIds = useMemo(() => {
    if (!Array.isArray(firstSelect)) return [];
    return firstSelect.map((b) => b.id);
  }, [firstSelect]);

  const { captureScreen, exportToPDF, handleImageExport } = useImageExport(
    firstSelect.length > 0 ? firstSelect[0] : {},
    null,
    startDate,
    endDate
  );

  const handleDownloadImage = async () => {
    try {
      await captureScreen();
    } catch (error) {
      console.error("Error al descargar imagen del gráfico:", error);
    }
  };

  const handleExportPdf = async () => {
    try {
      await exportToPDF(graphRef);
    } catch (error) {
      console.error("Error al exportar PDF del gráfico:", error);
    }
  };

  const handleExportClick = () => {
    setShowReportInput((prev) => !prev);
    setButtonVisible(false);
  };

  const handleSendToReports = async () => {
    try {
      await handleImageExport(graphRef, {});
      setShowReportInput(false);
    } catch (error) {
      console.error("Error al enviar gráfico a reportes:", error);
    }
  };

const handleApply = () => {
  if (!branchIds.length) {
    Toast.fire({
      icon: "warning",
      title: "Seleccione al menos una sucursal",
      timer: 2000,
    });
    return;
  }

  if (!startDate || !endDate) {
    Toast.fire({
      icon: "warning",
      title: "Seleccione el período A",
      timer: 2000,
    });
    return;
  }

  fetchEnergyMatrix();
};

  const fetchEnergyMatrix = async () => {
    try {
      setLoading(true);

      const params = {
        branchIds,
        startDate: startDate?.toISOString().split("T")[0],
        endDate: endDate?.toISOString().split("T")[0],
        compareStartDate: compareStartDate
          ? compareStartDate.toISOString().split("T")[0]
          : null,
        compareEndDate: compareEndDate
          ? compareEndDate.toISOString().split("T")[0]
          : null,
      };

      const result = await getEnergyMatrixSummary(params, userAccessToken);

      setData(result);
    } catch (error) {
      console.error("Error fetching energy matrix:", error);
      Toast.fire({
        icon: "error",
        title: "Error al cargar matriz energética",
        timer: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const energyCarriers = useMemo(() => {
    if (!data?.carriers) return [];
    return data.carriers.filter(
      (c) => c.id === "electricity" || c.id === "gas",
    );
  }, [data]);

  const electricityCarrier = useMemo(() => {
    if (!data?.carriers) return null;
    return data.carriers.find((c) => c.id === "electricity") || null;
  }, [data]);

  const gapKWh = electricityCarrier?.gap_kWh ?? 0;

  const isSignificantGap = Math.abs(gapKWh) > 0;

  const isFacturaMayor =
    (electricityCarrier?.kWh_invoice ?? 0) >
    (electricityCarrier?.kWh_meter ?? 0);

  const costCarriers = useMemo(() => {
    if (!data?.carriers) return [];
    return data.carriers.filter(
      (c) => c.id === "electricity" || c.id === "gas",
    );
  }, [data]);


  // HANDLERS
  const handlePeriodAChange = (_frec, s, e) => {
    setStartDate(s);
    setEndDate(e);
  };

  const handlePeriodBChange = (_frec, s, e) => {
    setCompareStartDate(s);
    setCompareEndDate(e);
  };

  const multipleBranches = branchIds && branchIds.length > 1;

  return (
    <div className="energy-matrix-container">
      <div className="em-header">
        <h1>Matriz Energética</h1>

        <div className="measurement">
          {/* Planta */}
          <div className="em-filters-left">
            <DragDropCompanies
              setFirstSelect={setFirstSelect}
              firstSelect={firstSelect}
              maxSelections={1}
            />
          </div>

          {/* Fechas */}
          <div className="em-filters-right">
            <div className="em-date-group">
              <label>Período A</label>
              <FrequencyComponent
                onValuesChange={handlePeriodAChange}
                startDate={startDate}
                endDate={endDate}
                isAnalysis={true}
              />
            </div>

              <div className="em-date-group">
                <label>Período B</label>
                <FrequencyComponent
                  onValuesChange={handlePeriodBChange}
                  startDate={compareStartDate}
                  endDate={compareEndDate}
                  isAnalysis={true}
                />
              </div>
              <button
                className="em-apply-btn"
                onClick={handleApply}
              >
                <Check size={14} style={{ marginRight: "4px" }} />
                Aplicar
              </button>
          </div>
        </div>
      </div>

      {loading && <div className="energy-matrix-loading">Cargando...</div>}
      {!loading && branchIds.length === 0 && (
        <div className="energy-matrix-empty">
          Seleccione una sucursal para visualizar la matriz energética.
        </div>
      )}
      {showComparison && (
        <div className="em-period-context">
          Comparando:
          <strong>
            {" "}
            Período A ({formatDate(startDate)} – {formatDate(endDate)})
          </strong>{" "}
          vs
          <strong>
            {" "}
            Período B ({formatDate(compareStartDate)} –{" "}
            {formatDate(compareEndDate)})
          </strong>
        </div>
      )}

      {!loading && data && (
        <>
          {/* KPIs */}
          <div className="em-kpi-section">
            <KpiCard
              label="Energía Total"
              value={data?.totals?.kWh_equiv ?? 0}
              unit="kWh eq."
              delta={
                data?.comparison?.enabled
                  ? data?.comparison?.delta?.totals?.kWh_equiv
                  : null
              }
              comparisonEnabled={data?.comparison?.enabled}
            />

            <KpiCard
              label="Costo Total"
              value={data?.totals?.cost ?? 0}
              unit="$"
              delta={
                data?.comparison?.enabled
                  ? data?.comparison?.delta?.totals?.cost
                  : null
              }
              comparisonEnabled={data?.comparison?.enabled}
            />

            <KpiCard
              label="Emisiones CO₂"
              value={(data?.totals?.co2 ?? 0) / 1000}
              unit="t CO₂"
              delta={
                data?.comparison?.enabled
                  ? (data?.comparison?.delta?.totals?.co2 ?? 0) / 1000
                  : null
              }
              comparisonEnabled={data?.comparison?.enabled}
            />
          </div>

          <div className="em-electricity-notes">
            <h4>📌 Implicaciones Analíticas:</h4>
            <ul>
              <li>
                <strong>Energía total:</strong> Se calcula priorizando la facturación. Si no hay factura, se usa la medición.
              </li>

              <li>
                <strong>Costo total:</strong> Se calcula siempre con la facturación.
              </li>

              <li>
                <strong>Distribución por grupos y equipos (gráfico):</strong> Usa medición operativa.
              </li>

              {isSignificantGap && (
                <li className="em-note-warning">
                  {isFacturaMayor
                    ? " Factura es mayor a medición medida."
                    : " Medición es mayor a lo facturado."}
                </li>
              )}
            </ul>
          </div>

          {data?.comparison?.enabled && data?.comparison?.drivers && (
            <div className="em-drivers-section">
              <h3>Factores del cambio de costo energético</h3>
              <DriversWaterfall drivers={data.comparison.drivers} />
            </div>
          )}

          {/* TABLA MATRIZ (bloque central principal) */}
          <div className="em-matrix-section">
            <CarrierTable carriers={data?.carriers || []} />
            {multipleBranches && (
              <BranchBenchmarkTable branches={data?.branches || []} />
            )}
          </div>

          {/* Electricidad validación */}
          {data?.carriers?.find((c) => c.id === "electricity") && (
            <ElectricityInfo
              electricity={data.carriers.find((c) => c.id === "electricity")}
            />
          )}

          {data?.hierarchy.length > 0 && (
            <>
              <div id="graph" className="em-chart-card" ref={graphRef}>
                <h3>Distribución del consumo por grupo y equipo (medición operativa)</h3>
                <p className="em-chart-caption">
                  Visualiza cómo se distribuye el consumo energético total desde
                  el grupo principal hasta cada subgrupo y equipo individual.
                </p>
                <SunburstChart data={data.hierarchy} />
              </div>

              <ChartControls
                onCaptureScreen={handleDownloadImage}
                onExportPDF={handleExportPdf}
                showExportInput={showReportInput}
                onExportClick={handleExportClick}
                onExportSubmit={handleSendToReports}
                buttonVisible={buttonVisible}
                nameInputRef={exportNameRef}
                hideChartTypes={true}
                hideCustomDashboard={true}
              />

              
            </>
          )}
          <div className="em-chart-card">
                <h3>Diagrama de dispositivos</h3>
                <p className="em-chart-caption">
                  Visualiza las relaciones entre dispositivos y su estado en la medición.
                </p>
                <DeviceGraph data={data.device_resolution?.[0]} />
              </div>
          {/* Charts (secundario) */}
          <div className="em-chart-section">
            <EnergyMixDonut carriers={energyCarriers} />
            <CostMixDonut carriers={costCarriers} />
          </div>
        </>
      )}
    </div>
  );
};

export default EnergyMatrix;
